import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import useIsNA from "@dana/Common/Hooks/useIsNA";
import { filterPageTypeByUserRoles } from "@dana/Common/NavigationHelpers";
import DanaMenu from "@dana/Components/DanaMenu/DanaMenu";
import ChevronSmall from "@dana/Icons/ChevronSmall";
import User from "@dana/MobiusOverrides/Icons/User";
import colorPallete, { elevation } from "@dana/MobiusOverrides/styles/variables";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import toggleMyaccountMenu from "@dana/Store/Data/MyAccountMenu/Handlers/ToggleMyAccountMenuHandler";
import { getCookie, removeCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrentUserIsGuest,
    getIsPunchOutSession,
    getSession,
    getSettingsCollection
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Menu, { MappedLink, MenuPresentationProps } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    myAccountSequence = "myAccountSequence",
}

const mapStateToProps = (state: ApplicationStateDana) => {
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";
    const orderSettings = getSettingsCollection(state).orderSettings;
    const session = getSession(state);
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;
    const isVmiAdmin = session?.userRoles?.toLowerCase().indexOf("vmi_admin") !== -1;
    const isVmiUser = session?.userRoles?.toLowerCase().indexOf("vmi_user") !== -1;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    const danaSettings = getDanaGeneralSettings(state);
    const isEMEARegion = useIsEmea(state);
    const isNARegion = useIsNA(state);

    return {
        danaSettings,
        userRoles: session?.userRoles?.split(", ") ?? [],
        userType: session?.properties?.userType ?? "",
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        firstName: state.context.session.firstName,
        createAccountUrl: createAccountPageLink,
        myaccoutMenuIsopen: state.myAccountMenuState.isOpen,
        isPriceListEnabled: danaSettings?.isPriceListEnabled,
        isPriceListEnabledOnlyForInternal: danaSettings?.isFuturePriceListEnabledForInternalUserOnly,
        isEMEARegion,
        isNARegion,
        isAuthenticated,
        displayVmiNavigation:
            currentMode === "Vmi" && orderSettings.vmiEnabled && isAuthenticated && (isVmiAdmin || isVmiUser),
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    toggleMyaccountMenu,
    setNavDrawerIsOpen,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    signInWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    createAccountTitle?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    menuHeaderListStyle?: MenuPresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {},
    titleClickable: {},
    titleIcon: {
        size: 24,
    },
    titleTypography: {},
    createAccountTitle: {},
    signOutClickable: {},
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

interface ConditionalItem {
    title: string;
    replacement: MappedLink;
    condition: boolean;
    removeWhenFalse?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;


export const HeaderSignIn = ({
    userRoles,
    userType,
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    navDrawerIsOpen,
    setNavDrawerIsOpen,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    firstName,
    isPriceListEnabled,
    isPriceListEnabledOnlyForInternal,
    displayVmiNavigation,
    createAccountUrl,
    toggleMyaccountMenu,
    myaccoutMenuIsopen,
    isEMEARegion,
    isNARegion,
    isAuthenticated,
    danaSettings
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);


    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const toggleMenu = () => {
        setIsMenuOpen((prev: boolean) => !prev);

        if (navDrawerIsOpen) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
        }
    };

    useEffect(() => {
        if (!myaccoutMenuIsopen) {
            setIsMenuOpen(myaccoutMenuIsopen);
        }
    }, [myaccoutMenuIsopen]);
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            if (
                isMenuOpen &&
                triggerRef.current &&
                menuRef.current &&
                !triggerRef.current.contains(target) &&
                !menuRef.current.contains(target)
            ) {
                setIsMenuOpen(false);
            }
        };
        if (isMenuOpen) {
            toggleMyaccountMenu({ isOpen: isMenuOpen });
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isMenuOpen]);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const isGuestUser = !includeAccountMenu || !userName || (userName && currentUserIsGuest);
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = User;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];


    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css``,
            },
            clickableProps: {
                css: css``,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    const getSignOutMenuItem = (title: "Sign out"): MappedLink => {
        return {
            title: translate(title),
            type: "signouttype",
            clickableProps: {
                css: css``,
                onClick: () => {
                    onSignOutHandler();
                },
            },
        };
    };
    const getSettingMenuItem = (title: "Settings"): MappedLink => {
        return {
            title: translate(title),
            children: [
                {
                    title: translate("PriceToggle"),
                    type: "pricetoggletype",
                },
            ],
        };
    };

    menuItems = filterPageTypeByUserRoles(menuItems, userRoles?.map(x => x.trim()) ?? []);
    const mapPriceListFileName = danaSettings?.mapPriceListFileName;
    const priceListItems: MappedLink[] = [
        { title: "Current Price List", url: "/MyAccount/DownloadPriceList/CurrentPriceList" },
        ...(isPriceListEnabled &&
            (!isPriceListEnabledOnlyForInternal || userType === "Internal")
            ? [{ title: "Future Price List", url: "/MyAccount/DownloadPriceList/FuturePriceList" }]
            : []),
    ];

    const conditionalItems: ConditionalItem[] = [
        {
            title: translate("Overstock"),
            replacement: { title: translate("Overstock Sale"), url: "/overstock" },
            condition: isNARegion && isAuthenticated,
            removeWhenFalse: true,
        },
        {
            title: "Download Lead Times",
            replacement: { title: "Download Lead Times", url: "/MyAccount/DownloadLeadTimes" },
            condition: !isGuestUser && (userType === "Internal" || userType === "Customer"),
            removeWhenFalse: true,
        },
        {
            title: "Download Price List",
            replacement: {
                title: "Download Price List",
                url: "/MyAccount/DownloadPriceList",
                children: priceListItems,
            },
            condition: userRoles.includes("Dana_DownloadPriceFile"),
            removeWhenFalse: true,
        },
        {
            title: translate("Core Return"),
            replacement: { title: translate("Core Return"), url: "/MyAccount/CoreReturn" },
            condition: userRoles.includes("Dana_CoreReturn") && !isEMEARegion,
            removeWhenFalse: true,
        },
        {
            title: translate("API Developer"),
            replacement: {
                title: translate("API Developer"),
                url: "/MyAccount/APIDeveloper",
                children: [
                    {
                        title: translate("Non-Prod Portal"),
                        url: danaSettings?.apimNonProdAPIPortal,
                        openInNewWindow: true,
                    },
                    {
                        title: translate("Prod Portal"),
                        url: danaSettings?.apimProdAPIPortal,
                        openInNewWindow: true,
                    },
                    { title: translate("API Key"), url: "/MyAccount/APIDeveloper/apikey" },
                ],
            },
            condition: userRoles.includes("Dana_Developer") && !isEMEARegion,
            removeWhenFalse: true,
        },
        {
            title: translate("Co Op"),
            replacement: { title: translate("Co Op"), url: "/MyAccount/CoOp" },
            condition: userRoles.includes("Dana_Co-op") && !isEMEARegion,
            removeWhenFalse: true,
        },
        {
            title: translate("Order Literature"),
            replacement: { title: translate("Order Literature"), url: danaSettings?.orderLiteratureLink },
            condition: userRoles.includes("Dana_OrderLiterature") && !isEMEARegion,
            removeWhenFalse: true,
        },
        {
            title: translate("Tradeshows"),
            replacement: { title: translate("Tradeshows"), url: danaSettings?.tradeshowsLink },
            condition: userRoles.includes("Dana_Tradeshows") && isNARegion && isAuthenticated,
            removeWhenFalse: true,
        },
        {
            title: translate("Settings"),
            replacement: getSettingMenuItem("Settings"),
            condition: userType === "Internal" || (userType === "Customer" && userRoles && userRoles.length > 0 && userRoles.map(x => x.trim()).includes("Dana_SeePrice")),
            removeWhenFalse: true,
        },
    ];

    const mergedMenuItems = useMemo(() => {
        let result = [...menuItems];

        conditionalItems.forEach(({ title, replacement, condition, removeWhenFalse }) => {
            const index = result.findIndex(item => item.title === title);
            if (index !== -1) {
                if (condition) {
                    result = [...result.slice(0, index), replacement, ...result.slice(index + 1)];
                } else if (removeWhenFalse) {
                    result = [...result.slice(0, index), ...result.slice(index + 1)];
                }
            } else if (condition) {
                // Special handling for Overstock - insert after View Credits
                if (title === "Overstock") {
                    const viewCreditsIndex = result.findIndex(item => item.title === "View Credits");
                    if (viewCreditsIndex !== -1) {
                        result = [...result.slice(0, viewCreditsIndex + 1), replacement, ...result.slice(viewCreditsIndex + 1)];
                    } else {
                        result = [...result, replacement];
                    }
                } else {
                    result = [...result, replacement];
                }
            }
        });

        return result;
    }, [menuItems, userRoles, userType, isGuestUser, isPriceListEnabled, isEMEARegion, isNARegion, isAuthenticated, danaSettings]);



    let finalMenuItems = mergedMenuItems;
   

    if (!currentMode || currentMode === "Storefront") {
        finalMenuItems = finalMenuItems.filter(pageLink => pageLink.type !== "VmiUsersPage");

        if (showInventoryToggle && displayPricingAndInventory) finalMenuItems = [...finalMenuItems, getMenuItem("Inventory")];
        if (showPricingToggle && displayPricingAndInventory) finalMenuItems = [...finalMenuItems, getMenuItem("Pricing")];
    }


    finalMenuItems = [...finalMenuItems, getSignOutMenuItem("Sign out")];

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push(`${signInUrl}`);
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
        }
    };

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Login");
    removeCookie("isauthenticated", { path: "/" });
    if (userName && !currentUserIsGuest) {
        signInStatusText = `${translate("Hi ")}${firstName}`;
        setCookie("isauthenticated", "true", { path: "/" });
    }

    let createAccountText = translate("Create Account");
    if (userName && !currentUserIsGuest) {
        createAccountText = translate("Manage Account");
        setCookie("isauthenticated", "true", { path: "/" });
    }

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl?.url);
    };

    return (
        <>
            <StyledWrapper {...styles.signOutWrapper} className={isGuestUser ? "for-guest" : "for-authenticated"}>

                {displayVmiNavigation && (
                    <Menu
                        className="hide-on-mobile hide-on-tablet accountMenu verticalMenuList"
                        descriptionId="accountMenu"
                        menuItems={[
                            {
                                title: translate("Sign Out"),
                                type: "signouttype",
                                clickableProps: {
                                    onClick: () => {
                                        onSignOutHandler();
                                    },
                                },
                            },
                        ]}
                        width={194}
                        displayChangeCustomerLink={displayChangeCustomerLink}
                        maxDepth={1}
                        menuTrigger={
                            <Clickable role="button" {...styles.titleClickable} data-test-selector="header_signIn">
                                {showIcon && (
                                    <Icon
                                        {...styles.titleIcon}
                                        src={icon}
                                        className="siginUserIcon userAuthenticated"
                                    />
                                )}
                                {showLabel && (
                                    <>
                                        <StyledWrapper {...styles.signInWrapper}>
                                            <Typography
                                                {...styles.titleTypography}
                                                data-test-selector="header_userName"
                                                className="username-logged-in"
                                            >
                                                {signInStatusText}&nbsp;
                                            </Typography>
                                            <Typography {...styles.createAccountTitle} className="create-account-title">
                                                <Typography>{createAccountText}</Typography>
                                                <Icon
                                                    size={16}
                                                    color="white"
                                                    src={ChevronSmall}
                                                    className="create-account-icon"
                                                />{" "}
                                            </Typography>
                                        </StyledWrapper>
                                    </>
                                )}
                            </Clickable>
                        }
                    />
                )}
                {!displayVmiNavigation && (
                    <>
                        {includeAccountMenu && userName && !currentUserIsGuest && (
                            <div ref={menuRef}>
                                <DanaMenu
                                    className="accountMenu verticalMenuList"
                                    descriptionId="accountMenu"
                                    menuItems={finalMenuItems}
                                    width={194}
                                    displayChangeCustomerLink={displayChangeCustomerLink}
                                    maxDepth={1}
                                    ref={menuRef}
                                    isOpen={isMenuOpen}
                                    menuTrigger={
                                        <div ref={triggerRef}>
                                            <Clickable
                                                role="button"
                                                ref={triggerRef}
                                                {...styles.titleClickable}
                                                data-test-selector="header_signIn"
                                                tabIndex={0}
                                                onClick={(e: { stopPropagation: () => void }) => {
                                                    e.stopPropagation();
                                                    toggleMenu();
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        setIsMenuOpen(prev => !prev);
                                                    }
                                                    if (e.key === "Escape") {
                                                        setIsMenuOpen(false);
                                                    }
                                                }}
                                            >
                                                {showIcon && (
                                                    <Icon
                                                        {...styles.titleIcon}
                                                        src={icon}
                                                        className="siginUserIcon userAuthenticated"
                                                    />
                                                )}
                                                {showLabel && (
                                                    <>
                                                        <StyledWrapper
                                                            {...styles.signInWrapper}
                                                            className="myaccount-user-loggedin-wrapper"
                                                        >
                                                            <Typography
                                                                {...styles.titleTypography}
                                                                data-test-selector="header_userName"
                                                            >
                                                                {signInStatusText}&nbsp;
                                                            </Typography>
                                                            <Typography
                                                                {...styles.createAccountTitle}
                                                                className="create-account-title"
                                                            >
                                                                <Typography>{createAccountText}</Typography>
                                                                <Icon
                                                                    size={16}
                                                                    color="white"
                                                                    src={ChevronSmall}
                                                                    className="create-account-icon"
                                                                />{" "}
                                                            </Typography>
                                                        </StyledWrapper>
                                                    </>
                                                )}
                                            </Clickable>
                                        </div>
                                    }
                                    cssOverrides={{
                                        wrapper: css`
                                            min-width: 0;
                                        `,
                                        menu: css`
                                            right: -8px;
                                            box-shadow: ${elevation.dp08};
                                            padding: 0;
                                            border: 1px solid ${colorPallete.grey300};
                                            border-bottom-left-radius: 8px;
                                            border-bottom-right-radius: 8px;
                                        `,
                                        menuItem: css`
                                            a {
                                                min-height: usnet;
                                                padding: 8px 8px 8px 16px;

                                                &:hover {
                                                    background: ${colorPallete.grey300};

                                                    &::before {
                                                        content: none;
                                                    }
                                                }
                                            }
                                        `,
                                    }}
                                    data-test-selector="header_accountMenu"
                                    {...styles.menuHeaderListStyle}
                                />
                            </div>
                        )}
                        {(!includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                            <>
                                <Clickable
                                    {...styles.titleClickable}
                                    onClick={onSignInHandler}
                                    href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                                    data-test-selector="header_signIn"
                                    className="guestUser"
                                >
                                    {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                                    {showLabel && (
                                        <Typography className="textWrapper">
                                            <Typography {...styles.createAccountTitle}>
                                                {signInStatusText}&nbsp;
                                            </Typography>
                                        </Typography>
                                    )}
                                </Clickable>
                                {/* Create Account link */}
                                <Clickable
                                    {...styles.titleClickable}
                                    onClick={handleCreateNewAccount}
                                    href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                                    data-test-selector="header_createAccount"
                                    className="guestUser"
                                >
                                    {showLabel && (
                                        <Typography className="textWrapper">
                                            <Typography {...styles.createAccountTitle}>
                                                {translate("Create Account")}
                                            </Typography>
                                        </Typography>
                                    )}
                                </Clickable>
                            </>
                        )}
                    </>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
